import { EMAIL_RE, MAX_EMAIL_LEN } from "../shared/validate.js";
import { SMS_CONSENT_TEXT_VERSION } from "../shared/consentText.js";
import { isIntent, type Intent } from "../shared/intents.js";

// Pure, DOM-free core of the landing-page contact form: the goal->intent catalog the select is
// rendered from, client-side pre-validation mirroring the server's rules (shared regex — no
// drift), payload construction, idempotency-key policy, and the HTTP-status->UI-state matrix.
// The DOM bootstrap (contactMain.ts) composes these; nothing here touches window/document.

export interface GoalOption {
  intent: Intent;
  label: string;
}

// The six goals the landing page exposes, in display order. Values are the wire intents the
// Contact API accepts (commercial:manage and land:buy exist server-side but are not surfaced).
export const GOAL_OPTIONS: readonly GoalOption[] = [
  { intent: "commercial:sell", label: "Sell a commercial property" },
  { intent: "commercial:buy", label: "Buy a commercial property" },
  { intent: "residential:sell", label: "Sell my home" },
  { intent: "residential:buy", label: "Buy a home" },
  { intent: "land:sell", label: "Sell land" },
  { intent: "residential:manage", label: "Get help with my rental properties" },
] as const;

export interface FormFields {
  name: string;
  email: string;
  phone: string;
  intent: string;
  smsConsent: boolean;
}

export type FieldError = { field: "name" | "email" | "phone" | "intent"; message: string };

export const MAX_NAME_LEN = 200;
export const PHONE_ERROR = "Enter a 10-digit US phone number";

// NANP-only conversion mirroring the server's rule: tolerate ()-. and spaces, accept 10
// digits or 11 starting with 1, produce +1XXXXXXXXXX. Anything else (incl. non-US +44…)
// is rejected client-side; the server independently enforces the same bound (AC-1.1).
export function toE164(raw: string): string | null {
  const trimmed = raw.trim();
  if (!/^[+\d()\-. ]*$/.test(trimmed)) return null;
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (hasPlus) {
    return digits.length === 11 && digits.startsWith("1") ? `+${digits}` : null;
  }
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

export function validateFields(fields: FormFields): FieldError[] {
  const errors: FieldError[] = [];
  const name = fields.name.trim();
  if (!name || name.length > MAX_NAME_LEN) {
    errors.push({ field: "name", message: "Please tell us your name (200 characters max)." });
  }
  const email = fields.email.trim();
  if (!email || email.length > MAX_EMAIL_LEN || !EMAIL_RE.test(email)) {
    errors.push({ field: "email", message: "Please enter a valid email address." });
  }
  if (toE164(fields.phone) === null) {
    errors.push({ field: "phone", message: PHONE_ERROR });
  }
  if (!isIntent(fields.intent)) errors.push({ field: "intent", message: "Please pick what you want to do." });
  return errors;
}

export interface ContactPayload {
  name: string;
  email: string;
  mobile: string;
  intent: Intent;
  preferredChannel: "email";
  smsConsent: boolean;
  consentTextVersion?: string;
  turnstileToken?: string;
}

export function buildPayload(fields: FormFields, turnstileToken: string | undefined): ContactPayload {
  const payload: ContactPayload = {
    name: fields.name.trim(),
    email: fields.email.trim(),
    mobile: toE164(fields.phone) ?? "",
    intent: fields.intent as Intent,
    preferredChannel: "email",
    smsConsent: fields.smsConsent === true,
  };
  // Version travels only with an actual opt-in; the server compares it against its own
  // authoritative version (drift metric) and never persists the client value.
  if (payload.smsConsent) payload.consentTextVersion = SMS_CONSENT_TEXT_VERSION;
  if (turnstileToken) payload.turnstileToken = turnstileToken;
  return payload;
}

// One idempotency key per distinct submission content: the same key is reused across retries of
// identical fields (server dedupes to one lead), and a fresh key is minted when the content
// changes (the server 409s a reused key with different fields). Canonical phone and the consent
// flag are part of the content — a consent change is a NEW submission, never a replay (AC-1.2).
export function fieldsFingerprint(fields: FormFields): string {
  return JSON.stringify([
    fields.name.trim(),
    fields.email.trim().toLowerCase(),
    toE164(fields.phone) ?? "",
    fields.intent,
    fields.smsConsent === true,
  ]);
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export type SubmitOutcome =
  | { kind: "success"; leadId: string }
  | { kind: "invalid"; errors: Array<{ field: string; message: string }> }
  | { kind: "blocked" } // 403: Turnstile failed/fail-closed
  | { kind: "key-conflict" } // 409: retry once with a fresh key
  | { kind: "retryable" } // 413/429/5xx or malformed body
  | { kind: "network" };

export function mapContactResponse(status: number, body: unknown): SubmitOutcome {
  if (status === 200) {
    const leadId = (body as { leadId?: string })?.leadId;
    return { kind: "success", leadId: typeof leadId === "string" ? leadId : "" };
  }
  if (status === 400) {
    const errors = (body as { errors?: Array<{ field: string; message: string }> })?.errors;
    return { kind: "invalid", errors: Array.isArray(errors) ? errors : [] };
  }
  if (status === 403) return { kind: "blocked" };
  if (status === 409) return { kind: "key-conflict" };
  return { kind: "retryable" };
}
